export type SkipTraceResource = {
  label: string;
  href: string;
  description: string;
};

export type SkipTracePack = {
  address: string;
  county: string;
  city: string;
  state: string;
  locationHint: string;
  ownerHint: string;
  steps: string[];
  resources: SkipTraceResource[];
};

type SkipTraceInput = {
  address: string;
  county?: string;
  city?: string;
  state?: string;
  ownerName?: string;
};

const googleSearch = (query: string) =>
  `https://www.google.com/search?q=${encodeURIComponent(query)}`;

export function buildFreeSkipTracePack(input: SkipTraceInput): SkipTracePack {
  const address = input.address.trim();
  const county = input.county?.trim() || "";
  const city = input.city?.trim() || "";
  const state = input.state?.trim() || "";
  const ownerName = input.ownerName?.trim() || "";

  const locationBits = [county, city, state].filter(Boolean);
  const locationHint = locationBits.length ? locationBits.join(", ") : "Unknown jurisdiction";
  const countyPhrase = [county || city, state].filter(Boolean).join(" ").trim();
  const propertyQuery = [address, countyPhrase, "assessor", "owner"]
    .filter(Boolean)
    .join(" ")
    .trim();

  const ownerSearchQuery = ownerName
    ? `${ownerName} ${city || county || state || ""}`.trim()
    : address;

  const resources: SkipTraceResource[] = [
    {
      label: "County GIS / Assessor",
      href: googleSearch(propertyQuery),
      description: "Search the parcel or tax record first to find the legal owner and mailing address.",
    },
    {
      label: "Google the property address",
      href: googleSearch(`${address} assessor property appraiser owner`),
      description: "Useful when you do not yet know the county site or exact office name.",
    },
    {
      label: "TruePeopleSearch",
      href: googleSearch(`site:truepeoplesearch.com \"${ownerSearchQuery}\"`),
      description: "Search the owner name after you pull it from the assessor record.",
    },
    {
      label: "Whitepages",
      href: googleSearch(`site:whitepages.com \"${ownerSearchQuery}\"`),
      description: "Cross-check the owner name, phone numbers, and known relatives.",
    },
    {
      label: "Facebook",
      href: googleSearch(`site:facebook.com \"${ownerSearchQuery}\"`),
      description: "Look for a matching profile, business page, or family connection.",
    },
    {
      label: "LinkedIn",
      href: googleSearch(`site:linkedin.com/in \"${ownerSearchQuery}\"`),
      description: "Helpful when the owner is a business owner or uses a professional profile.",
    },
  ];

  const steps = [
    "Start with the county GIS / assessor record for the property address.",
    "Copy the owner name and mailing address from the public tax record.",
    "Search the owner name on TruePeopleSearch and Whitepages to find phone numbers.",
    "Search the owner name on Facebook and LinkedIn for social or business contact paths.",
    "Cross-check every hit against at least one other source before you call or text.",
  ];

  return {
    address,
    county,
    city,
    state,
    locationHint,
    ownerHint: ownerName || "Public record owner (enter the assessor name once you find it)",
    steps,
    resources,
  };
}
